import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
  Typography
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPrint } from '@fortawesome/pro-light-svg-icons'
import {
  downloadRubricReferencePdf,
  downloadStudentProgressPdf,
  downloadClassProgressZip
} from '../../../../../../services/maestro/maestro.service'
import { downloadBlob } from '../../../../../../utils/file'

// MF-187 — Print rubric PDFs from a Unit of Learning. Visible from any tab of the class marking
// view (top bar, before Cancel). Two documents: Progress report (per student / whole-class ZIP)
// and Rubric reference (blank, all levels, whole unit). The BE streams the PDF/ZIP; we name the
// download client-side from the UoL / class / student we already have in redux.
const cleanName = (s) =>
  String(s || '')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const PrintRubricButton = () => {
  const { curriculumId, classId, ulId, studentId } = useParams()
  const students = useSelector((s) => s?.classMarking?.students || [])
  const ulName = useSelector((s) => s?.class?.unitOfLearning?.name) || 'Unit of Learning'
  const classCode = useSelector((s) => s?.class?.form?.code) || 'Class'

  const [open, setOpen] = useState(false)
  const [docType, setDocType] = useState('progress') // 'progress' | 'reference'
  const [scope, setScope] = useState('student') // 'student' | 'class'
  const [selStudent, setSelStudent] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const openDialog = () => {
    setSelStudent(studentId || students[0]?._id || '')
    setDocType('progress')
    setScope('student')
    setError(null)
    setOpen(true)
  }
  const close = () => {
    if (!loading) setOpen(false)
  }

  const studentName = (id) => {
    const st = students.find((x) => String(x._id) === String(id))
    return st ? st.fullName || `${st.preferredName || st.firstName} ${st.lastName}` : ''
  }

  const generate = async () => {
    setLoading(true)
    setError(null)
    try {
      let blob
      let filename
      if (docType === 'reference') {
        blob = await downloadRubricReferencePdf(curriculumId, classId, ulId)
        filename = `${cleanName(`Rubric Reference - ${ulName}`)}.pdf`
      } else if (scope === 'class') {
        blob = await downloadClassProgressZip(curriculumId, classId, ulId)
        filename = `${cleanName(`Rubric Progress - ${classCode} - ${ulName}`)}.zip`
      } else {
        if (!selStudent) {
          setError('Please select a student.')
          setLoading(false)
          return
        }
        blob = await downloadStudentProgressPdf(curriculumId, classId, ulId, selStudent)
        filename = `${cleanName(`Rubric Progress - ${studentName(selStudent)} - ${ulName}`)}.pdf`
      }
      downloadBlob(blob, filename)
      setOpen(false)
    } catch (e) {
      setError('Could not generate the document. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Button
        size='small'
        variant='outlined'
        color='primary'
        className='shadow-sm bg-background'
        startIcon={<FontAwesomeIcon icon={faPrint} />}
        onClick={openDialog}
      >
        Print
      </Button>

      <Dialog open={open} onClose={close} maxWidth='xs' fullWidth>
        <DialogTitle>Print rubric</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant='caption' color='textSecondary'>
            Document
          </Typography>
          <RadioGroup value={docType} onChange={(e) => setDocType(e.target.value)}>
            <FormControlLabel
              value='progress'
              control={<Radio size='small' />}
              label='Progress report (with marking)'
            />
            <FormControlLabel
              value='reference'
              control={<Radio size='small' />}
              label='Rubric reference (blank — all levels)'
            />
          </RadioGroup>

          {docType === 'progress' ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant='caption' color='textSecondary'>
                For
              </Typography>
              <RadioGroup value={scope} onChange={(e) => setScope(e.target.value)}>
                <FormControlLabel value='student' control={<Radio size='small' />} label='This student' />
                <FormControlLabel
                  value='class'
                  control={<Radio size='small' />}
                  label='Whole class (a PDF per student, zipped)'
                />
              </RadioGroup>
              {scope === 'student' && (
                <TextField
                  select
                  size='small'
                  fullWidth
                  label='Student'
                  value={selStudent}
                  onChange={(e) => setSelStudent(e.target.value)}
                  sx={{ mt: 1 }}
                >
                  {students.map((st) => (
                    <MenuItem key={st._id} value={st._id}>
                      {st.label || st.fullName}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          ) : (
            <Typography variant='caption' color='textSecondary' sx={{ display: 'block', mt: 1 }}>
              Lists every level of each skill for this unit — applies to the whole class, not one student.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={generate}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <FontAwesomeIcon icon={faPrint} />}
          >
            {loading ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PrintRubricButton
